import { useEffect, useState } from "react";
import api, { naira, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet as WalletIcon, Plus, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { toast } from "sonner";
import RefundNote from "@/components/RefundNote";

export default function Wallet() {
  const [data, setData] = useState(null);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = () => api.get("/wallet/me").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => { refresh(); }, []);

  const topup = async (amount) => {
    if (!Number.isFinite(amount) || amount < 500 || amount > 100000) {
      toast.error("Enter an amount between ₦500 and ₦100,000.");
      return;
    }
    setLoading(true);
    try {
      const callback_url = `${window.location.origin}/wallet/callback`;
      const { data: resp } = await api.post("/wallet/topup/initialize", { amount_naira: amount, callback_url });
      window.location.href = resp.authorization_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start top-up");
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-ink grain">
        <Navbar />
        <div className="pt-32 px-8 text-center text-foreground/60">Loading wallet…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-3xl mx-auto pb-20" data-testid="wallet-page">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Your wallet</p>
          <h1 className="font-display text-4xl font-black">Balance &amp; top-ups</h1>
          <p className="text-foreground/60 mt-2">Top up your wallet to rent films in one tap, with no extra checkout.</p>
        </div>

        {/* Balance card */}
        <div className="rounded-2xl p-8 bg-gradient-to-br from-terracotta/20 via-ember/10 to-transparent border border-terracotta/30 mb-6 relative overflow-hidden" data-testid="wallet-balance-card">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
          <div className="relative flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-widest text-foreground/55 mb-1.5 flex items-center gap-1.5">
                <WalletIcon className="w-3 h-3" /> Available balance
              </p>
              <p className="font-display text-5xl font-black text-gold" data-testid="wallet-balance-amount">
                {naira(data.wallet_balance_naira)}
              </p>
            </div>
            <div className="text-right text-xs text-foreground/55 leading-relaxed max-w-[180px]">
              <p>Spend from <strong className="text-foreground/80">₦100</strong> per rental.</p>
              <p>Lifetime access — rewatch any time.</p>
            </div>
          </div>
        </div>

        {/* Top-up panel */}
        <div className="rounded-2xl p-6 sm:p-8 bg-surface border border-white/10 mb-6" data-testid="topup-panel">
          {data.first_topup_bonus_eligible && (
            <div className="mb-5 rounded-lg border border-gold/40 bg-gold/10 p-3 flex items-center gap-2.5" data-testid="first-topup-bonus-badge">
              <span className="text-gold text-xl">🎁</span>
              <p className="text-xs text-foreground/85">
                <strong className="text-gold">First top-up bonus.</strong> Add at least
                <strong className="text-gold"> {naira(data.first_topup_bonus_threshold_naira)}</strong> to your wallet and we'll credit you an extra
                <strong className="text-gold"> {naira(data.first_topup_bonus_naira)}</strong> on the house.
              </p>
            </div>
          )}
          <h2 className="font-display text-xl font-bold mb-1">Add funds</h2>
          <p className="text-xs text-foreground/55 mb-5">Min ₦500 · Max ₦100,000 per top-up · powered by Paystack</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
            {(data.topup_chips_naira || []).map((amount) => (
              <Button
                key={amount}
                onClick={() => topup(amount)}
                disabled={loading}
                variant="outline"
                className="h-14 border-white/15 hover:border-terracotta hover:bg-terracotta/10 text-base font-display font-bold"
                data-testid={`topup-chip-${amount}`}
              >
                + {naira(amount)}
              </Button>
            ))}
          </div>

          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="Custom amount"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              min={500}
              max={100000}
              className="bg-ink border-white/10 h-12"
              data-testid="topup-custom-input"
            />
            <Button
              onClick={() => topup(Number(custom))}
              disabled={loading || !custom}
              className="bg-terracotta hover:bg-ember text-white h-12 px-5"
              data-testid="topup-custom-btn"
            >
              <Plus className="w-4 h-4" /> Top up
            </Button>
          </div>
          <RefundNote className="mt-3" testId="topup-refund-policy-link" />
        </div>

        {/* History */}
        <div className="rounded-2xl bg-surface border border-white/10 overflow-hidden" data-testid="wallet-history">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="font-display text-lg font-bold">Recent activity</h2>
          </div>
          {data.history.length === 0 ? (
            <p className="px-6 py-10 text-center text-foreground/50 text-sm">No wallet activity yet. Add funds above to start renting in one tap.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {data.history.map((tx) => (
                <li key={tx.id} className="flex items-center justify-between gap-3 px-6 py-3" data-testid={`tx-${tx.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {tx.kind === "topup" ? (
                      <span className="w-8 h-8 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0">
                        <ArrowDownCircle className="w-4 h-4" />
                      </span>
                    ) : tx.kind === "bonus" ? (
                      <span className="w-8 h-8 rounded-full bg-gold/20 text-gold flex items-center justify-center shrink-0">🎁</span>
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center shrink-0">
                        <ArrowUpCircle className="w-4 h-4" />
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm truncate">
                        {tx.kind === "topup" ? "Wallet top-up" : tx.kind === "bonus" ? (tx.note || "Bonus credit") : `Rented · ${tx.film_title || "film"}`}
                      </p>
                      <p className="text-[11px] text-foreground/50">{new Date(tx.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <p className={`font-display font-bold text-sm ${tx.kind === "topup" ? "text-emerald-400" : tx.kind === "bonus" ? "text-gold" : "text-foreground/85"}`}>
                    {tx.kind === "spend" ? "−" : "+"}{naira(tx.amount_naira)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
